import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { ColumnsType } from 'antd/es/table';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { formatDateTime } from './assetDetail.utils';

type UseAssetDetailComponentColumnsProps = {
  onEdit?: (component: AssetDetailComponent) => void;
  onDelete?: (component: AssetDetailComponent) => void;
};

function formatOcppRoleLabel(
  value: string | null | undefined,
  t: (key: string, fallback: string) => string,
): string {
  if (value === 'controller') {
    return t('detail.components.types.controller', 'Controller');
  }
  if (value === 'connector') {
    return t('detail.components.types.connector', 'Connector');
  }
  return value ?? '-';
}

export function useAssetDetailComponentColumns({
  onEdit,
  onDelete,
}: UseAssetDetailComponentColumnsProps = {}): ColumnsType<AssetDetailComponent> {
  const { t } = useTranslation('assets');

  return useMemo(() => {
    const columns: ColumnsType<AssetDetailComponent> = [
      {
        title: t('detail.components.table.ocppRole', 'OCPP role'),
        dataIndex: 'componentType',
        key: 'componentType',
        width: 140,
        render: (value: string | null) => {
          if (!value) return '-';
          return (
            <Tag color={value === 'controller' ? 'purple' : 'blue'}>
              {formatOcppRoleLabel(value, t)}
            </Tag>
          );
        },
      },
      {
        title: t('detail.components.table.name', 'Component'),
        dataIndex: 'name',
        key: 'name',
        width: 220,
      },
      {
        title: t('detail.components.table.assetType', 'Asset type'),
        key: 'assetType',
        width: 180,
        render: (_: unknown, record) =>
          record.assetContext?.assetType.name ?? '-',
      },
      {
        title: t('detail.components.table.subsystem', 'Subsystem'),
        key: 'subsystem',
        width: 180,
        render: (_: unknown, record) =>
          record.assetContext?.subsystem.name ?? '-',
      },
      {
        title: t('detail.components.table.componentClass', 'Component class'),
        key: 'kbComponentClass',
        width: 200,
        render: (_: unknown, record) =>
          record.assetContext?.componentClass.name ??
          record.componentClass?.name ??
          '-',
      },
      {
        title: t('detail.components.table.kbComponentType', 'Component type'),
        key: 'kbComponentType',
        width: 200,
        render: (_: unknown, record) =>
          record.assetContext?.componentType.name ?? '-',
      },
      {
        title: t('detail.components.table.connectorId', 'Connector ID'),
        dataIndex: 'connectorId',
        key: 'connectorId',
        width: 130,
        render: (value: number | null) =>
          value === null || value === undefined ? '-' : value,
      },
      {
        title: t('detail.components.table.ocppId', 'OCPP ID'),
        dataIndex: 'ocppId',
        key: 'ocppId',
        width: 200,
        render: (value: string | null) => value ?? '-',
      },
      {
        title: t('detail.components.table.controller', 'Controller'),
        dataIndex: 'controller',
        key: 'controller',
        width: 260,
        render: (value: AssetDetailComponent['controller']) => {
          if (!value) return '-';
          return value.ocppId ? `${value.name} (${value.ocppId})` : value.name;
        },
      },
      {
        title: t('detail.components.table.serialNumber', 'Serial number'),
        dataIndex: 'serialNumber',
        key: 'serialNumber',
        width: 180,
        render: (value: string | null) => value ?? '-',
      },
      {
        title: t('detail.components.table.installedAt', 'Installed at'),
        dataIndex: 'installedAt',
        key: 'installedAt',
        width: 190,
        render: (value: string | null) => formatDateTime(value),
      },
      {
        title: t('detail.components.table.removedAt', 'Removed at'),
        dataIndex: 'removedAt',
        key: 'removedAt',
        width: 190,
        render: (value: string | null) => formatDateTime(value),
      },
    ];

    if (onEdit || onDelete) {
      columns.push({
        title: t('detail.components.table.actions', 'Actions'),
        key: 'actions',
        width: 56,
        align: 'center' as const,
        render: (_: unknown, record: AssetDetailComponent) => (
          <TableRowActionsMenu
            items={[
              ...(onEdit
                ? [
                    {
                      key: 'edit',
                      label: t('detail.components.actions.edit', 'Edit'),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(record),
                    },
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      key: 'delete',
                      label: t('detail.components.actions.delete', 'Delete'),
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(record),
                    },
                  ]
                : []),
            ]}
          />
        ),
      });
    }

    return columns;
  }, [t, onEdit, onDelete]);
}
